import { createComputed, createEffect, createMemo, createResource, For, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useLanguage } from "@/context/language"
import { listReportShares, requestApprovals, shareReport, type CoworkArtifact } from "@/cowork/api"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { DialogBody, DialogHeader, DialogTitle, DialogV2 } from "@opencode-ai/ui/v2/dialog-v2"

export function ApprovalRequestForm(props: {
  artifacts: Pick<CoworkArtifact, "id" | "title">[]
  allowSharing?: boolean
  onRequest: (artifactID: string, emails: string[], message: string | undefined, purpose: "share" | "approval") => Promise<void>
}) {
  const language = useLanguage()
  let disposed = false
  onCleanup(() => {
    disposed = true
  })
  const [store, setStore] = createStore({
    artifactID: "", emails: "", message: "", requesting: false, error: "",
    purpose: (props.allowSharing ? "share" : "approval") as "share" | "approval",
  })
  const sharing = () => store.purpose === "share"
  const selectedArtifact = createMemo(
    () => props.artifacts.find((artifact) => artifact.id === store.artifactID) ?? props.artifacts[0],
  )
  createEffect(() => {
    const artifact = selectedArtifact()
    if (artifact && artifact.id !== store.artifactID) setStore("artifactID", artifact.id)
  })
  const submitRequest = async (event: SubmitEvent) => {
    event.preventDefault()
    if (disposed || store.requesting) return
    const artifact = selectedArtifact()
    const emails = store.emails
      .split(/[\n,;]/)
      .map((email) => email.trim())
      .filter(Boolean)
    if (!artifact || !emails.length) return
    setStore({ requesting: true, error: "" })
    try {
      await props.onRequest(artifact.id, emails, store.message.trim() || undefined, store.purpose)
      if (!disposed) setStore({ emails: "", message: "" })
    } catch (error) {
      if (!disposed) setStore("error", error instanceof Error ? error.message : String(error))
    } finally {
      if (!disposed) setStore("requesting", false)
    }
  }
  return (
    <form onSubmit={submitRequest}>
      <div class="mb-4">
        <h3 class="text-15-medium text-v2-text-text-base">
          {language.t(sharing() ? "cowork.report.share.title" : "cowork.approval.request.title")}
        </h3>
        <p class="mt-1 text-12-regular text-v2-text-text-muted">
          {language.t(sharing() ? "cowork.report.share.description" : "cowork.approval.request.description")}
        </p>
      </div>
      <Show when={props.allowSharing}>
        <fieldset class="mb-4" disabled={store.requesting}>
          <legend class="mb-2 text-12-medium text-v2-text-text-muted">{language.t("cowork.report.share.purpose")}</legend>
          <div class="flex flex-wrap gap-4">
            <For each={["share", "approval"] as const}>
              {(purpose) => (
                <label class="flex items-center gap-2 text-13-regular">
                  <input type="radio" name="report-share-purpose" value={purpose} checked={store.purpose === purpose}
                    onChange={() => setStore({ purpose, error: "" })} />
                  {language.t(purpose === "share" ? "cowork.report.share.title" : "cowork.report.share.requestApproval")}
                </label>
              )}
            </For>
          </div>
        </fieldset>
      </Show>
      <div class="grid gap-3 lg:grid-cols-[1fr_1.2fr]">
        <label class="flex flex-col gap-2">
          <span class="text-12-medium text-v2-text-text-muted">{language.t("cowork.approval.request.report")}</span>
          <select
            class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2 text-13-regular text-v2-text-text-base outline-none"
            value={store.artifactID}
            disabled={store.requesting || props.artifacts.length <= 1}
            onChange={(event) => setStore("artifactID", event.currentTarget.value)}
          >
            <For each={props.artifacts}>{(artifact) => <option value={artifact.id}>{artifact.title}</option>}</For>
          </select>
        </label>
        <label class="flex flex-col gap-2">
          <span class="text-12-medium text-v2-text-text-muted">
            {language.t(sharing() ? "cowork.report.share.recipients" : "cowork.approval.request.reviewers")}
          </span>
          <input
            class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2 text-13-regular text-v2-text-text-base outline-none"
            value={store.emails}
            disabled={store.requesting}
            placeholder={language.t(sharing() ? "cowork.report.share.recipientsPlaceholder" : "cowork.approval.request.reviewers.placeholder")}
            onInput={(event) => setStore("emails", event.currentTarget.value)}
          />
        </label>
      </div>
      <label class="mt-3 flex flex-col gap-2">
        <span class="text-12-medium text-v2-text-text-muted">{language.t("cowork.approval.request.message")}</span>
        <textarea
          class="min-h-20 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-2 py-2 text-13-regular text-v2-text-text-base outline-none"
          value={store.message}
          disabled={store.requesting}
            placeholder={language.t(sharing() ? "cowork.report.share.messagePlaceholder" : "cowork.approval.request.message.placeholder")}
          onInput={(event) => setStore("message", event.currentTarget.value)}
        />
      </label>
      <Show when={store.error}>
        <p role="alert" class="mt-3 text-12-regular text-v2-state-fg-danger">
          {store.error}
        </p>
      </Show>
      <div class="mt-4 flex justify-end">
        <button
          type="submit"
          class="rounded-md bg-v2-button-primary-bg px-4 py-2 text-13-medium text-v2-button-primary-text disabled:opacity-50"
          disabled={!selectedArtifact() || !store.emails.trim() || store.requesting}
        >
          {language.t(store.requesting ? "cowork.approval.request.sending" : sharing() ? "cowork.report.share.send" : props.allowSharing ? "cowork.report.share.requestApproval" : "cowork.approval.requestReview")}
        </button>
      </div>
    </form>
  )
}

export function ReportShareDialog(props: { artifact: CoworkArtifact; active: () => boolean }) {
  const language = useLanguage()
  const dialog = useDialog()
  const artifact = props.artifact
  const [state, setState] = createStore({ sent: false })
  const [history, historyActions] = createResource(() => listReportShares(artifact.project_id, artifact.id, artifact.company_id))
  let disposed = false
  onCleanup(() => {
    disposed = true
  })
  createComputed(() => {
    if (!props.active()) dialog.close()
  })
  return (
    <DialogV2 fit containerClass="!h-auto max-h-[calc(100vh_-_16px)] !w-[min(calc(100vw_-_16px),640px)]">
      <DialogHeader closeLabel={language.t("common.close")}>
        <DialogTitle>{language.t("cowork.enterprise.reports.share")}</DialogTitle>
      </DialogHeader>
      <DialogBody class="flex-none overflow-y-auto p-5">
        <ApprovalRequestForm
          artifacts={[artifact]}
          allowSharing
          onRequest={async (artifactID, emails, message, purpose) => {
            if (disposed || !props.active()) return
            setState("sent", false)
            if (purpose === "approval") {
              await requestApprovals(artifact.project_id, artifactID, emails, message, artifact.company_id)
              if (!disposed && props.active()) dialog.close()
              return
            }
            const result = await shareReport(artifact.project_id, artifactID, emails, message, artifact.company_id)
            if (disposed || !props.active()) return
            historyActions.mutate([...result, ...(history.error ? [] : history() ?? [])])
            void Promise.resolve(historyActions.refetch()).catch(() => undefined)
            if (result.some((item) => item.status !== "sent")) {
              throw new Error(language.t("cowork.report.share.deliveryFailed"))
            }
            setState("sent", true)
          }}
        />
        <Show when={state.sent}>
          <p role="status" class="mt-3 text-13-regular text-v2-state-fg-success">{language.t("cowork.report.share.sent")}</p>
        </Show>
        <details class="mt-5 border-t border-v2-border-border-base pt-4" open>
          <summary class="cursor-pointer text-13-medium">{language.t("cowork.report.share.history")}</summary>
          <Show when={!history.loading} fallback={<p class="mt-3 text-12-regular">{language.t("common.loading")}</p>}>
            <Show when={!history.error} fallback={<p role="alert" class="mt-3 text-12-regular">{language.t("cowork.report.share.historyError")}</p>}>
              <Show when={history()?.length} fallback={<p class="mt-3 text-12-regular text-v2-text-text-muted">{language.t("cowork.report.share.historyEmpty")}</p>}>
                <ul class="mt-3 max-h-48 space-y-3 overflow-y-auto text-12-regular">
                  <For each={history()}>{(item) => (
                    <li class="flex flex-wrap justify-between gap-2">
                      <span class="break-all">{item.recipient_email}</span>
                      <span class="text-v2-text-text-muted">
                        {language.t(`cowork.report.share.status.${item.status}`)} · {new Date(item.sent_at || item.created_at).toLocaleString(language.locale())}
                      </span>
                    </li>
                  )}</For>
                </ul>
              </Show>
            </Show>
          </Show>
        </details>
      </DialogBody>
    </DialogV2>
  )
}
